import { PII_PROPERTY_DENYLIST, type AnalyticsRecord } from "./privacy";
import { scrubAnalyticsProps } from "./propsAllowlist";

export interface PosthogCaptureResult {
  event: string;
  properties?: AnalyticsRecord;
  $set?: AnalyticsRecord;
  $set_once?: AnalyticsRecord;
  [key: string]: unknown;
}

export interface PosthogPrivacyOptions {
  autocapture: false;
  capture_pageview: false;
  capture_pageleave: false;
  disable_session_recording: true;
  person_profiles: "identified_only";
  property_denylist: string[];
  before_send: (event: PosthogCaptureResult | null) => PosthogCaptureResult | null;
}

/**
 * Privacy-hardened PostHog init options: no autocapture, pageview, pageleave or
 * session recording (all collect URLs / DOM / behaviour), a personal-data
 * property denylist, and a `before_send` sanitizer as the final guard.
 */
export function posthogPrivacyOptions(): PosthogPrivacyOptions {
  return {
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    person_profiles: "identified_only",
    property_denylist: [...PII_PROPERTY_DENYLIST],
    before_send: sanitizePosthogEvent,
  };
}

/**
 * PostHog `before_send` hook. Returns a copy of the captured event with all
 * non-allowlisted keys removed from its event properties, from the top-level
 * person `$set` / `$set_once` payloads (the `identify(id, props)` path), and
 * from the nested `properties.$set` / `properties.$set_once` payloads (the
 * `people.set(props)` path). It also injects `$geoip_disable: true` so PostHog
 * skips server-side geoip enrichment. A `null` input is PostHog's signal that
 * the event was already dropped, and is passed through untouched.
 */
export function sanitizePosthogEvent(
  event: PosthogCaptureResult | null,
): PosthogCaptureResult | null {
  if (!event) {
    return event;
  }
  const next: PosthogCaptureResult = { ...event };
  next.properties = sanitizeEventProperties(next.properties);
  if (next.$set) {
    next.$set = scrubAnalyticsProps(next.$set);
  }
  if (next.$set_once) {
    next.$set_once = scrubAnalyticsProps(next.$set_once);
  }
  return next;
}

/**
 * Non-`$` keys posthog-js writes into `properties` itself. `token` is required
 * for ingestion (the SDK drops the whole event when a before_send hook removes
 * it) and `distinct_id` binds the event to the anonymous person.
 */
const SDK_TRANSPORT_KEYS: readonly string[] = ["token", "distinct_id"];

function transportKeys(properties: AnalyticsRecord): AnalyticsRecord {
  const kept = SDK_TRANSPORT_KEYS.filter((key) => typeof properties[key] === "string").map(
    (key) => [key, properties[key]] as const,
  );
  return Object.fromEntries(kept);
}

function sanitizeEventProperties(properties: AnalyticsRecord | undefined): AnalyticsRecord {
  const next: AnalyticsRecord = properties
    ? { ...scrubAnalyticsProps(properties), ...transportKeys(properties) }
    : {};
  if (next.$set) {
    next.$set = scrubAnalyticsProps(next.$set as AnalyticsRecord);
  }
  if (next.$set_once) {
    next.$set_once = scrubAnalyticsProps(next.$set_once as AnalyticsRecord);
  }
  next.$geoip_disable = true;
  return next;
}
